import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AtomLoader from "@/components/AtomLoader";
import LoginPage from "@/pages/LoginPage";

const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const SchoolLabDashboard = lazy(() => import("@/pages/SchoolLabDashboard"));
const SchoolDashboard = lazy(() => import("@/pages/SchoolDashboard"));
const WeGuideDashboard = lazy(() => import("@/pages/WeGuideDashboard"));

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <AtomLoader text="Verifying session..." />
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;

  const getDashboardRedirect = () => {
    if (!user) return "/";
    switch (user.role) {
      case "super_admin": return "/admin";
      case "school_lab": return "/lab";
      case "school_authority": return "/school";
      case "weguide_authority": return "/weguide";
      default: return "/";
    }
  };

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <AtomLoader text="Loading interface..." />
      </div>
    }>
      <Routes>
        <Route path="/" element={user ? <Navigate to={getDashboardRedirect()} replace /> : <LoginPage />} />
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/lab" element={
          <ProtectedRoute allowedRoles={["school_lab"]}>
            <SchoolLabDashboard />
          </ProtectedRoute>
        } />
        <Route path="/school" element={
          <ProtectedRoute allowedRoles={["school_authority"]}>
            <SchoolDashboard />
          </ProtectedRoute>
        } />
        <Route path="/weguide" element={
          <ProtectedRoute allowedRoles={["weguide_authority"]}>
            <WeGuideDashboard />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
